import axios from 'axios';
import { CoinMarketData, CoinGeckoCategory } from '../types/coingecko';
import config from '../config/config';

export const fetchCoinMarkets = async (): Promise<CoinMarketData[]> => {
  const response = await axios.get(`${config.get('coingecko.baseUrl')}/coins/markets`, {
    headers: {
      'x-cg-demo-api-key': config.get('coingecko.credentials'),
    },
    params: {
      vs_currency: 'usd',
      order: 'market_cap_desc',
      per_page: 250,
      page: 1,
      sparkline: false,
      locale: 'en',
    },
  });
  return response.data;
};

export const fetchCoinsByIds = async (ids: string[]): Promise<Record<string, CoinMarketData>> => {
  if (!ids.length) return {};

  const response = await axios.get(`${config.get('coingecko.baseUrl')}/coins/markets`, {
    headers: {
      'x-cg-demo-api-key': config.get('coingecko.credentials'),
    },
    params: {
      vs_currency: 'usd',
      ids: ids.join(','),
      order: 'market_cap_desc',
      sparkline: false,
      locale: 'en',
    },
  });

  const coinsMap: Record<string, CoinMarketData> = {};
  response.data.forEach((coin: CoinMarketData) => {
    coinsMap[coin.id] = coin;
  });

  return coinsMap;
};

export const fetchCoinById = async (id: string): Promise<CoinMarketData> => {
  const coinResponse = await axios.get(`${config.get('coingecko.baseUrl')}/coins/${id}`, {
    headers: {
      'x-cg-demo-api-key': config.get('coingecko.credentials'),
    },
    params: {
      localization: false,
      tickers: false,
      community_data: false,
      developer_data: false,
    },
  });

  const { data: coinData } = coinResponse;

  // Transform the response to match CoinMarketData structure
  return {
    id: coinData.id,
    symbol: coinData.symbol,
    name: coinData.name,
    image: coinData.image.large,
    current_price: coinData.market_data.current_price.usd,
    market_cap: coinData.market_data.market_cap.usd,
    market_cap_rank: coinData.market_cap_rank,
    fully_diluted_valuation: coinData.market_data.fully_diluted_valuation?.usd || null,
    total_volume: coinData.market_data.total_volume.usd,
    high_24h: coinData.market_data.high_24h.usd,
    low_24h: coinData.market_data.low_24h.usd,
    price_change_24h: coinData.market_data.price_change_24h,
    price_change_percentage_24h: coinData.market_data.price_change_percentage_24h,
    market_cap_change_24h: coinData.market_data.market_cap_change_24h,
    market_cap_change_percentage_24h: coinData.market_data.market_cap_change_percentage_24h,
    circulating_supply: coinData.market_data.circulating_supply,
    total_supply: coinData.market_data.total_supply,
    max_supply: coinData.market_data.max_supply,
    ath: coinData.market_data.ath.usd,
    ath_change_percentage: coinData.market_data.ath_change_percentage.usd,
    ath_date: coinData.market_data.ath_date.usd,
    atl: coinData.market_data.atl.usd,
    atl_change_percentage: coinData.market_data.atl_change_percentage.usd,
    atl_date: coinData.market_data.atl_date.usd,
    last_updated: coinData.last_updated,
  };
};

export const fetchCategories = async (): Promise<CoinGeckoCategory[]> => {
  const response = await axios.get(`${config.get('coingecko.baseUrl')}/coins/categories/list`, {
    headers: {
      'x-cg-demo-api-key': config.get('coingecko.credentials'),
    },
  });
  return response.data;
};
